import React, { createContext, useContext, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DailyRingData {
  ring_request_id: string;
  call_id: string;
  current_level: number;
  timeout_seconds: number;
  available_users: any[];
}

interface DailyRingContextType {
  dailyRingData: DailyRingData | null;
  isStarting: boolean;
  startDailyRing: (params: {
    contactId?: string;
    leadId?: string;
    dealId?: string;
    targetUserId?: string;
  }) => Promise<void>;
  closeModal: () => void;
}

const DailyRingContext = createContext<DailyRingContextType | undefined>(undefined);

export function DailyRingProvider({ children }: { children: ReactNode }) {
  const [dailyRingData, setDailyRingData] = useState<DailyRingData | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  const startDailyRing = async (params: {
    contactId?: string;
    leadId?: string;
    dealId?: string;
    targetUserId?: string;
  }) => {
    if (isStarting || dailyRingData) {
      console.log('[DailyRingContext] ⚠️ Already processing, ignoring request');
      return;
    }

    setIsStarting(true);

    try {
      console.log('[DailyRingContext] 📞 Calling start-video-ring with daily provider...', params);

      const result = await supabase.functions.invoke('start-video-ring', {
        body: {
          contact_id: params.contactId,
          lead_id: params.leadId,
          deal_id: params.dealId,
          target_user_id: params.targetUserId,
          metadata: { video_provider: 'daily' },
        },
      });

      console.log('[DailyRingContext] 📦 Full result:', result);

      if (result.error) {
        console.error('[DailyRingContext] ❌ Error:', result.error);
        throw result.error;
      }

      if (!result.data) {
        console.error('[DailyRingContext] ❌ No data returned');
        throw new Error('No data returned from function');
      }

      console.log('[DailyRingContext] ✅ Setting dailyRingData:', result.data);
      setDailyRingData(result.data);
    } catch (error) {
      console.error('[DailyRingContext] ❌ Caught error:', error);
      toast.error('Erro ao iniciar videochamada');
    } finally {
      setIsStarting(false);
    }
  };

  const closeModal = () => {
    console.log('[DailyRingContext] 🚪 Closing modal');
    setDailyRingData(null);
    setIsStarting(false);
  };

  return (
    <DailyRingContext.Provider value={{ dailyRingData, isStarting, startDailyRing, closeModal }}>
      {children}
    </DailyRingContext.Provider>
  );
}

export function useDailyRing() {
  const context = useContext(DailyRingContext);
  if (context === undefined) {
    throw new Error('useDailyRing must be used within a DailyRingProvider');
  }
  return context;
}
